import { formatCurrency } from '@/utils/numberFormat';

const DEFAULT_CURRENCY = 'MXN';

const toNumber = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toSafeCount = (value) => {
  return Math.max(0, Math.trunc(toNumber(value)));
};

const normalizeCurrencyCode = (value) => {
  const code = String(value || '').trim().toUpperCase();
  return code === '' ? DEFAULT_CURRENCY : code;
};

const escapeRegExp = (value) => {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const orderCurrencyTotals = (totals = []) => {
  const safeTotals = Array.isArray(totals) ? totals : [];

  return [...safeTotals].sort((left, right) => {
    const leftCode = normalizeCurrencyCode(left?.currency);
    const rightCode = normalizeCurrencyCode(right?.currency);

    if (leftCode === DEFAULT_CURRENCY && rightCode !== DEFAULT_CURRENCY) {
      return -1;
    }

    if (rightCode === DEFAULT_CURRENCY && leftCode !== DEFAULT_CURRENCY) {
      return 1;
    }

    return leftCode.localeCompare(rightCode);
  });
};

const normalizeCurrencyTotals = (rawTotals = [], fallbackTotal = 0) => {
  const grouped = {};

  if (Array.isArray(rawTotals)) {
    rawTotals.forEach((item) => {
      const currency = normalizeCurrencyCode(item?.currency);
      const total = toNumber(item?.total ?? item?.amount);
      grouped[currency] = (grouped[currency] || 0) + total;
    });
  } else if (rawTotals && typeof rawTotals === 'object') {
    Object.entries(rawTotals).forEach(([currencyKey, total]) => {
      const currency = normalizeCurrencyCode(currencyKey);
      grouped[currency] = (grouped[currency] || 0) + toNumber(total);
    });
  }

  if (Object.keys(grouped).length === 0 && toNumber(fallbackTotal) !== 0) {
    grouped[DEFAULT_CURRENCY] = toNumber(fallbackTotal);
  }

  return orderCurrencyTotals(
    Object.entries(grouped).map(([currency, total]) => ({
      currency,
      total: toNumber(total),
    }))
  );
};

const buildCurrencySegments = (totalsByCurrency = [], fallbackTotal = 0) => {
  const normalizedTotals = normalizeCurrencyTotals(totalsByCurrency, fallbackTotal);

  if (normalizedTotals.length === 0) {
    return [formatCurrency(fallbackTotal || 0)];
  }

  const shouldShowCode = normalizedTotals.length > 1;

  return normalizedTotals.map((item) => {
    const currencyCode = normalizeCurrencyCode(item?.currency);
    const amount = formatCurrency(item?.total || 0, { currency: currencyCode });
    const normalizedAmount = String(amount || '').trim().toUpperCase().replace(/\u00A0/g, ' ');
    const currencyPrefixPattern = new RegExp(`^${escapeRegExp(currencyCode)}(?:\\s|\\$)`);
    const amountIncludesCode = currencyPrefixPattern.test(normalizedAmount);

    if (!shouldShowCode && currencyCode === DEFAULT_CURRENCY) {
      return amount;
    }

    if (amountIncludesCode) {
      return amount;
    }

    return `${currencyCode} ${amount}`;
  });
};

const formatVariationPercent = (value) => {
  const numeric = toNumber(value);
  const sign = numeric > 0 ? '+' : '';

  return `${sign}${numeric.toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;
};

export const createInitialPaymentReceiptsMetricsValues = () => ({
  totalCollected: 0,
  totalPending: 0,
  periodVariationPercent: 0,
  averagePayment: 0,
  paymentCount: 0,
  totalCollectedByCurrency: [],
  totalPendingByCurrency: [],
  averagePaymentByCurrency: [],
});

export const buildPaymentReceiptsKpiCards = (metricsValues, labels = {}) => {
  const safeMetrics = metricsValues && typeof metricsValues === 'object'
    ? metricsValues
    : createInitialPaymentReceiptsMetricsValues();

  const totalCollectedByCurrency = normalizeCurrencyTotals(
    safeMetrics.totalCollectedByCurrency,
    safeMetrics.totalCollected,
  );
  const totalPendingByCurrency = normalizeCurrencyTotals(
    safeMetrics.totalPendingByCurrency,
    safeMetrics.totalPending,
  );
  const averagePaymentByCurrency = normalizeCurrencyTotals(
    safeMetrics.averagePaymentByCurrency,
    safeMetrics.averagePayment,
  );

  const totalCollectedSegments = buildCurrencySegments(totalCollectedByCurrency, safeMetrics.totalCollected);
  const totalPendingSegments = buildCurrencySegments(totalPendingByCurrency, safeMetrics.totalPending);
  const averagePaymentSegments = buildCurrencySegments(averagePaymentByCurrency, safeMetrics.averagePayment);

  return [
    {
      title: labels.totalCollected || 'Total cobrado',
      value: totalCollectedSegments[0] || formatCurrency(safeMetrics.totalCollected),
      valueSegments: totalCollectedSegments,
      icon: 'fa-check-circle-o',
      iconBg: '#ecfcef',
      iconColor: '#1f9c3f',
    },
    {
      title: labels.totalPending || 'Total pendiente',
      value: totalPendingSegments[0] || formatCurrency(safeMetrics.totalPending),
      valueSegments: totalPendingSegments,
      icon: 'fa-clock-o',
      iconBg: '#fff7e8',
      iconColor: '#d88a1f',
    },
    {
      title: labels.periodVariation || 'Variacion periodo %',
      value: formatVariationPercent(safeMetrics.periodVariationPercent),
      icon: 'fa-line-chart',
      iconBg: '#eef2ff',
      iconColor: '#1d4ed8',
    },
    {
      title: labels.averagePayment || 'Pago promedio',
      value: averagePaymentSegments[0] || formatCurrency(safeMetrics.averagePayment),
      valueSegments: averagePaymentSegments,
      icon: 'fa-calculator',
      iconBg: '#edf8f5',
      iconColor: '#178f72',
    },
  ];
};

export const mapPaymentReceiptsMetricsPayload = (payload) => {
  const safePayload = payload && typeof payload === 'object' ? payload : {};

  return {
    totalCollected: toNumber(safePayload.totalCollected ?? safePayload.total_collected),
    totalPending: toNumber(safePayload.totalPending ?? safePayload.total_pending),
    periodVariationPercent: toNumber(safePayload.periodVariationPercent ?? safePayload.period_variation_percent),
    averagePayment: toNumber(safePayload.averagePayment ?? safePayload.average_payment),
    paymentCount: toSafeCount(safePayload.paymentCount ?? safePayload.payment_count),
    totalCollectedByCurrency: normalizeCurrencyTotals(
      safePayload.totalCollectedByCurrency ?? safePayload.total_collected_by_currency,
      safePayload.totalCollected ?? safePayload.total_collected,
    ),
    totalPendingByCurrency: normalizeCurrencyTotals(
      safePayload.totalPendingByCurrency ?? safePayload.total_pending_by_currency,
      safePayload.totalPending ?? safePayload.total_pending,
    ),
    averagePaymentByCurrency: normalizeCurrencyTotals(
      safePayload.averagePaymentByCurrency ?? safePayload.average_payment_by_currency,
      safePayload.averagePayment ?? safePayload.average_payment,
    ),
  };
};
